/**
 * Textliche Beitrags-Verweise klickbar machen (Alex, 17.08., Screenshot 119764:
 * „kannst du erkennen, wenn ein Agent postet, dass das ein Content-Beitrag ist
 * und die ID, und dass da ein Link dabei ist, mit dem ich auf den Post komme").
 *
 * Agenten schreiben Verweise als Fließtext („Post #4754", „Beitrag #1201") —
 * bisher war das tote Zeichenfolge, und der Operator musste die Adresse selbst
 * zusammensetzen. Hier wird daraus ein Markdown-Link auf den Beitrag.
 *
 * BEWUSST KONSERVATIV — nur Verweise MIT Präfix:
 * In dieser Föderation kollidieren die Nummernräume. „#1160" ist ein Issue,
 * „#4701" ein Content-Beitrag; beide erscheinen ständig im selben Gespräch.
 * Ein blindes Verlinken jeder `#\d+` würde also die Hälfte der Verweise auf
 * die falsche Stelle schicken — schlimmer als kein Link, weil ein falscher
 * Link wie ein richtiger aussieht. Deshalb: nur `Post|Beitrag|Content-Post
 * #\d+`, und `Issue|Ticket #\d+` wird ausdrücklich NICHT angefasst.
 *
 * Nicht angefasst werden außerdem: Code-Blöcke/-Spans (dort ist `#123` oft
 * Syntax), bereits bestehende Links und Bildunterschriften.
 */

/** Präfixe, die einen Content-Beitrag bezeichnen. `Issue`/`Ticket` fehlen hier
 *  absichtlich — siehe Kopfkommentar. */
const POST_REF = /\b(Content-Post|Content-Beitrag|Post|Beitrag|Posting)\s*#(\d{1,6})\b/gi;

/** Zeilenweise Maske: true, wenn die Zeile in einem ```-Block liegt. */
function fencedLines(md: string): boolean[] {
  const lines = md.split('\n');
  const mask: boolean[] = [];
  let inFence = false;
  for (const l of lines) {
    if (/^\s*```/.test(l)) { mask.push(true); inFence = !inFence; continue; }
    mask.push(inFence);
  }
  return mask;
}

/** Ersetzt außerhalb von Code-Spans; `segments` teilt an Backticks. */
function replaceOutsideCode(line: string, fn: (s: string) => string): string {
  return line
    .split(/(`[^`]*`)/g)
    .map((part) => (part.startsWith('`') ? part : fn(part)))
    .join('');
}

/**
 * Wandelt Beitrags-Verweise in Markdown-Links um.
 * `origin` ist der Content-Ursprung dieser Instanz (Dienst-Registry) — auf
 * einer Kundeninstanz zeigt der Link auf deren eigene content-api, nicht auf
 * die des Betreibers. Fehlt der Dienst (`null`), bleibt der Text unverändert:
 * kein Link ist besser als ein Link ins Leere.
 */
export function linkifyPostRefs(md: string, origin: string | null): string {
  if (!origin || !md || !md.includes('#')) return md;
  const mask = fencedLines(md);
  return md
    .split('\n')
    .map((line, i) => {
      if (mask[i]) return line;
      // Zeilen, die bereits einen Link auf denselben Beitrag tragen, in Ruhe
      // lassen — sonst entstünde ein Link im Link.
      if (/\]\(/.test(line) && /\/post\/\d+/.test(line)) return line;
      return replaceOutsideCode(line, (part) =>
        part.replace(POST_REF, (whole, word: string, id: string) =>
          `[${word} #${id}](${origin}/post/${id})`),
      );
    })
    .join('\n');
}
